import { IS_DEV } from '@/config/env';

export type LogLevel = 'info' | 'warn' | 'error';

export interface LogPayload {
    message: string;
    error?: unknown;
    extra?: Record<string, unknown>;
}

export type ErrorReporter = (error: Error, extra?: Record<string, unknown>) => void;

/**
 * Register once at app init to forward errors (e.g. Sentry):
 *
 *   import * as Sentry from "@sentry/react";
 *   logger.addErrorReporter((error, extra) => {
 *     Sentry.captureException(error, { extra });
 *   });
 */
class Logger {
    private readonly errorReporters: ErrorReporter[] = [];

    addErrorReporter(reporter: ErrorReporter): () => void {
        this.errorReporters.push(reporter);
        return () => {
            const index = this.errorReporters.indexOf(reporter);
            if (index !== -1) {
                this.errorReporters.splice(index, 1);
            }
        };
    }

    private notifyErrorReporters(error: Error, extra?: Record<string, unknown>): void {
        this.errorReporters.forEach((reporter) => {
            try {
                reporter(error, extra);
            } catch {
                // Reporters must never break the app
            }
        });
    }

    log(level: LogLevel, payload: LogPayload): void {
        if (IS_DEV) {
            // eslint-disable-next-line no-console -- Logger is the sole intentional console sink in the app.
            (console[level] as (...args: unknown[]) => void)(payload.message, payload.error, payload.extra);
        }

        if (level === 'error' && payload.error instanceof Error) {
            this.notifyErrorReporters(payload.error, {
                ...payload.extra,
                message: payload.message
            });
        }
    }

    info(message: string, extra?: Record<string, unknown>): void {
        this.log('info', { message, extra });
    }

    warn(message: string, extra?: Record<string, unknown>): void {
        this.log('warn', { message, extra });
    }

    error(message: string, err?: unknown, extra?: Record<string, unknown>): void {
        this.log('error', { message, error: err, extra });
    }
}

export const logger = new Logger();
